import { useCallback } from "react";

import { PartiallyRequired } from "@regal-voice/shared-types";
import { useNavigate } from "react-router-dom";

import {
    RVPaddedPage,
    useQueryParamsV6,
    hasMorePagination,
    FilterParams,
    OperatorType,
    QueryParams,
    getNonSearchFiltersFromQueryParams,
    RVLoadingGrid as LoadingGrid,
    usePageConfig,
} from "@regal-voice/ui";
import { ADSettingsPaths } from "Pages/settings/Pages/AgentDesktop/paths";
import { SETTINGS_NAV_TITLES } from "Pages/settings/settingsMetadata";
import { CommunicationTemplate } from "Types/CommunicationTemplate";

import { getColumns } from "./columns";
import { CommunicationTemplatesHeader } from "./Components/CommunicationTemplatesHeader";
import { useCommunicationTemplateListData } from "./Hooks/useCommunicationTemplateListData";

const SEARCHABLE_FIELDS: Array<string> = ["friendlyId", "name"];

const defaultQueryParams: PartiallyRequired<QueryParams, "page" | "pageSize" | "orderBy"> = {
    page: 1,
    pageSize: 25,
    orderBy: [
        {
            key: "name",
            value: "ASC",
        },
    ],
};

const EMPTY: Array<CommunicationTemplate> = [];

export default function CommunicationTemplates(): JSX.Element {
    usePageConfig({ pageTitle: SETTINGS_NAV_TITLES[ADSettingsPaths.templates] });

    const navigate = useNavigate();
    const { queryParams, setUiQueryParams } = useQueryParamsV6(defaultQueryParams);
    const filterValue = queryParams.filterBy?.find((value) => value.key === "name");
    const defaultSearch = filterValue?.value as string | undefined;

    const setFilterValue = useCallback(
        (value: string): void => {
            const filter: Array<FilterParams> = value
                ? [
                      {
                          key: "name",
                          operator: OperatorType.CONTAINS,
                          value,
                      },
                  ]
                : [];

            setUiQueryParams((prevParams) => ({
                ...prevParams,
                ...{
                    page: 1,
                    filterBy: getNonSearchFiltersFromQueryParams(
                        prevParams.filterBy?.filter((x) => !SEARCHABLE_FIELDS.includes(x.key))
                    ).concat(filter),
                },
            }));
        },
        [setUiQueryParams]
    );

    const { isLoading, isDeleting, findCommunicationTemplatesByBrand, removeCommunicationTemplate } =
        useCommunicationTemplateListData(queryParams);

    const communicationTemplateItems = findCommunicationTemplatesByBrand?.items || EMPTY;

    return (
        <RVPaddedPage>
            <CommunicationTemplatesHeader setFilterValue={setFilterValue} defaultSearch={defaultSearch} />

            <LoadingGrid
                rowKey={({ id }: CommunicationTemplate) => id}
                loading={isLoading}
                skeletonRowLines={2}
                columns={getColumns(navigate, queryParams, isDeleting, removeCommunicationTemplate)}
                columnsData={communicationTemplateItems}
                pageWidth="wide"
                tableSize="small"
                additionalFilters={SEARCHABLE_FIELDS}
                data-testid="communication-templates-grid"
                defaultParams={defaultQueryParams}
                totalRecords={hasMorePagination(
                    communicationTemplateItems,
                    queryParams,
                    findCommunicationTemplatesByBrand?.hasMore
                )}
            />
        </RVPaddedPage>
    );
}
